<h1>Manage System Messages</h1>

@defer (when gridView.length > 0) {
  <kendo-grid
    class="table-wrapper"
    [kendoGridBinding]="gridView"
    [sortable]="true"
    [resizable]="true"
    [pageSize]="pageSize"
    [pageable]="true"
    [columnMenu]="{ filter: true }"
    [scrollable]="'none'"
    (add)="addHandler($event)"
    (edit)="editHandler($event)"
    (cancel)="cancelHandler($event)"
    (save)="saveHandler($event)">

    <ng-template kendoGridToolbarTemplate>
      <!--
      Upgrading Kendo UI to v21 caused a focus problem.  The fix is to apply the kendoGridToolbarFocusable directive.
      This directive is only need if the kendoGridExcelCommand, kendoGridAddCommand or kendoGridPDFCommand are used in the toolbar.
      See ticket for more details: https://www.telerik.com/account/support-center/view-ticket/1706516
       -->
      <kendo-textbox
        kendoGridToolbarFocusable (click)="$event.stopPropagation()"
        [style.width.px]="350"
        placeholder="Search..."
        (valueChange)="onFilter($event)"></kendo-textbox>

      <kendo-grid-spacer></kendo-grid-spacer>

      <button kendoGridAddCommand>Add new</button>
    </ng-template>

    <kendo-grid-column
      field="status"
      title="Status"
      [width]="100">
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        @let status = dataItem.status;
        @switch (status) {
          @case (statusTypes.Current) {
            <kendo-chip size="large" rounded="full" fillMode="solid" themeColor="success" [id]="dataItem.id"
                        [class.hide]="editedRowIndex === rowIndex">
              {{ status }}
            </kendo-chip>
          }
          @case (statusTypes.Upcoming) {
            <kendo-chip size="large" rounded="full" fillMode="solid" themeColor="warning" [id]="dataItem.id"
                        [class.hide]="editedRowIndex === rowIndex">
              {{ status }}
            </kendo-chip>
          }
          @case (statusTypes.Expired) {
            <kendo-chip size="large" rounded="full" fillMode="solid" themeColor="error" [id]="dataItem.id"
                        [class.hide]="editedRowIndex === rowIndex">
              {{ status }}
            </kendo-chip>
          }
        }
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="message"
      title="Message">
      <ng-template
        kendoGridEditTemplate
        let-dataItem="dataItem"
        let-formGroup="formGroup">
        <kendo-label
          class="k-display-block"
          [for]="message"
          aria-label="Message"
          text=""/>
        <kendo-textarea
          #message
          placeholder="Enter message"
          [formControl]="formGroup!.get('message')"
          size="small"
          rounded="medium"
          fillMode="solid"/>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="messageType"
      title="Type"
      [width]="msgTypeColumnWidth">
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        @switch (dataItem.messageType) {
          @case (gsmType.ERROR) {
            <kendo-chip size="large" rounded="full" fillMode="solid" themeColor="error" [id]="dataItem.id"
                        [class.hide]="editedRowIndex === rowIndex">
              {{ dataItem.messageType }}
            </kendo-chip>
          }
          @case (gsmType.WARNING) {
            <kendo-chip size="large" rounded="full" fillMode="solid" themeColor="warning" [id]="dataItem.id"
                        [class.hide]="editedRowIndex === rowIndex">
              {{ dataItem.messageType }}
            </kendo-chip>
          }
          @case (gsmType.INFO) {
            <kendo-chip size="large" rounded="full" fillMode="solid" themeColor="info" [id]="dataItem.id"
                        [class.hide]="editedRowIndex === rowIndex">
              {{ dataItem.messageType }}
            </kendo-chip>
          }
        }
      </ng-template>

      <!-- Edit message type template -->
      <ng-template
        kendoGridEditTemplate
        let-dataItem="dataItem"
        let-formGroup="formGroup">
        <kendo-dropdownlist
          [data]="messageTypesArray"
          textField="value"
          valueField="key"
          [valuePrimitive]="true"
          [formControl]="formGroup!.get('messageType')">
        </kendo-dropdownlist>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="startDatetime"
      title="Start Date Time"
      [width]="dateColumnWidth">
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        <span [id]="dataItem.id"
              [class.hide]="editedRowIndex === rowIndex">{{ dataItem.startDatetime | date:'short' }}</span>
      </ng-template>

      <!-- Edit start date/time template -->
      <ng-template
        kendoGridEditTemplate
        let-dataItem="dataItem"
        let-formGroup="formGroup">
        <kendo-datetimepicker
          [class.error-field]="formGroup.hasError('dateRangeInvalid')"
          [value]="dataItem.startDatetime"
          [formControl]="formGroup!.get('startDatetime')"></kendo-datetimepicker>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="endDatetime"
      title="End Date Time"
      [width]="dateColumnWidth">
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        <span [id]="dataItem.id"
              [class.hide]="editedRowIndex === rowIndex">{{ dataItem.endDatetime | date:'short' }}</span>
      </ng-template>

      <!-- Edit start date/time template -->
      <ng-template
        kendoGridEditTemplate
        let-dataItem="dataItem"
        let-formGroup="formGroup">
        <kendo-datetimepicker
          [class.error-field]="formGroup.hasError('dateRangeInvalid')"
          [value]="dataItem.endDatetime"
          [formControl]="formGroup!.get('endDatetime')"></kendo-datetimepicker>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-command-column title="Edit" [width]="editColumnWidth">
      <ng-template kendoGridCellTemplate let-isNew="isNew">
        <button kendoGridEditCommand [primary]="true">Edit</button>
        <button kendoGridSaveCommand [disabled]="formGroup?.invalid ?? true">
          {{ isNew ? "Add" : "Update" }}
        </button>
        <button kendoGridCancelCommand>
          {{ isNew ? "Discard changes" : "Cancel" }}
        </button>
      </ng-template>
    </kendo-grid-command-column>

    <!-- Detail template -->
    <ng-template kendoGridDetailTemplate let-dataItem>
      <section>
        <p><span>ID:</span> <span>{{ dataItem.id }}</span></p>
        <p><span>Modified By:</span> <span>{{ dataItem.modifiedBy }}</span></p>
        <p><span>Modified Date:</span> <span>{{ dataItem.modifiedDate | date:'short' }}</span></p>
        <p><span>Created By:</span> <span>{{ dataItem.createdBy }}</span></p>
        <p><span>Create Date:</span> <span>{{ dataItem.createdDate | date:'short' }}</span></p>
      </section>
    </ng-template>

    <!-- Pager template -->
    <ng-template
      kendoPagerTemplate
      let-totalPages="totalPages"
      let-currentPage="currentPage">
      <kendo-pager-prev-buttons></kendo-pager-prev-buttons>
      <kendo-pager-numeric-buttons [buttonCount]="2"></kendo-pager-numeric-buttons>
      <kendo-pager-next-buttons></kendo-pager-next-buttons>
      <kendo-pager-input></kendo-pager-input>
      <kendo-pager-info></kendo-pager-info>
      <kendo-pager-page-sizes [pageSizes]="pageSizes"></kendo-pager-page-sizes>
    </ng-template>
  </kendo-grid>
} @placeholder {
  <!--
  This extra boilerplate is needed to work around a Kendo UI for Angular issue where the
  grid does not except the data properly when data is loaded asynchronously.  There could be some race conditions
  happening internally in the Kendo grid component that causes it to not render data correctly when the data is
  available at the time of initial rendering.  The fix is to defer the grid with the condition that the data has a length greater than 0.
  The placeholder block displays the grid when the data has no length.  We do this because the grid has a feature to show
  a "no data" message when there is no data, and we want to leverage that.  Plus, we have buttons on the top of the grid that
  should be visible even when there is no data.
  -->
  <kendo-grid
    class="table-wrapper"
    [kendoGridBinding]="gridView"
    [sortable]="true"
    [resizable]="true"
    [pageSize]="pageSize"
    [pageable]="true"
    [columnMenu]="{ filter: true }"
    [scrollable]="'none'"
    (add)="addHandler($event)"
    (edit)="editHandler($event)"
    (cancel)="cancelHandler($event)"
    (save)="saveHandler($event)">

    <ng-template kendoGridToolbarTemplate>
      <kendo-textbox
        [style.width.px]="350"
        placeholder="Search..."
        (valueChange)="onFilter($event)"></kendo-textbox>

      <kendo-grid-spacer></kendo-grid-spacer>

      <button kendoGridAddCommand>Add new</button>
    </ng-template>

    <kendo-grid-column
      field="status"
      title="Status"
      [width]="100">
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        @let status = dataItem.status;
        @switch (status) {
          @case (statusTypes.Current) {
            <kendo-chip size="large" rounded="full" fillMode="solid" themeColor="success" [id]="dataItem.id"
                        [class.hide]="editedRowIndex === rowIndex">
              {{ status }}
            </kendo-chip>
          }
          @case (statusTypes.Upcoming) {
            <kendo-chip size="large" rounded="full" fillMode="solid" themeColor="warning" [id]="dataItem.id"
                        [class.hide]="editedRowIndex === rowIndex">
              {{ status }}
            </kendo-chip>
          }
          @case (statusTypes.Expired) {
            <kendo-chip size="large" rounded="full" fillMode="solid" themeColor="error" [id]="dataItem.id"
                        [class.hide]="editedRowIndex === rowIndex">
              {{ status }}
            </kendo-chip>
          }
        }
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="message"
      title="Message">
      <ng-template
        kendoGridEditTemplate
        let-dataItem="dataItem"
        let-formGroup="formGroup">
        <kendo-label
          class="k-display-block"
          [for]="message"
          aria-label="Message"
          text=""/>
        <kendo-textarea
          #message
          placeholder="Enter message"
          [formControl]="formGroup!.get('message')"
          size="small"
          rounded="medium"
          fillMode="solid"/>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="messageType"
      title="Type"
      [width]="msgTypeColumnWidth">
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        @switch (dataItem.messageType) {
          @case (gsmType.ERROR) {
            <kendo-chip size="large" rounded="full" fillMode="solid" themeColor="error" [id]="dataItem.id"
                        [class.hide]="editedRowIndex === rowIndex">
              {{ dataItem.messageType }}
            </kendo-chip>
          }
          @case (gsmType.WARNING) {
            <kendo-chip size="large" rounded="full" fillMode="solid" themeColor="warning" [id]="dataItem.id"
                        [class.hide]="editedRowIndex === rowIndex">
              {{ dataItem.messageType }}
            </kendo-chip>
          }
          @case (gsmType.INFO) {
            <kendo-chip size="large" rounded="full" fillMode="solid" themeColor="info" [id]="dataItem.id"
                        [class.hide]="editedRowIndex === rowIndex">
              {{ dataItem.messageType }}
            </kendo-chip>
          }
        }
      </ng-template>

      <!-- Edit message type template -->
      <ng-template
        kendoGridEditTemplate
        let-dataItem="dataItem"
        let-formGroup="formGroup">
        <kendo-dropdownlist
          [data]="messageTypesArray"
          textField="value"
          valueField="key"
          [valuePrimitive]="true"
          [formControl]="formGroup!.get('messageType')">
        </kendo-dropdownlist>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="startDatetime"
      title="Start Date Time"
      [width]="dateColumnWidth">
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        <span [id]="dataItem.id"
              [class.hide]="editedRowIndex === rowIndex">{{ dataItem.startDatetime | date:'short' }}</span>
      </ng-template>

      <!-- Edit start date/time template -->
      <ng-template
        kendoGridEditTemplate
        let-dataItem="dataItem"
        let-formGroup="formGroup">
        <kendo-datetimepicker
          [class.error-field]="formGroup.hasError('dateRangeInvalid')"
          [value]="dataItem.startDatetime"
          [formControl]="formGroup!.get('startDatetime')"></kendo-datetimepicker>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-column
      field="endDatetime"
      title="End Date Time"
      [width]="dateColumnWidth">
      <ng-template kendoGridCellTemplate let-dataItem let-rowIndex="rowIndex">
        <span [id]="dataItem.id"
              [class.hide]="editedRowIndex === rowIndex">{{ dataItem.endDatetime | date:'short' }}</span>
      </ng-template>

      <!-- Edit start date/time template -->
      <ng-template
        kendoGridEditTemplate
        let-dataItem="dataItem"
        let-formGroup="formGroup">
        <kendo-datetimepicker
          [class.error-field]="formGroup.hasError('dateRangeInvalid')"
          [value]="dataItem.endDatetime"
          [formControl]="formGroup!.get('endDatetime')"></kendo-datetimepicker>
      </ng-template>
    </kendo-grid-column>

    <kendo-grid-command-column title="Edit" [width]="editColumnWidth">
      <ng-template kendoGridCellTemplate let-isNew="isNew">
        <button kendoGridEditCommand [primary]="true">Edit</button>
        <button kendoGridSaveCommand [disabled]="formGroup?.invalid ?? true">
          {{ isNew ? "Add" : "Update" }}
        </button>
        <button kendoGridCancelCommand>
          {{ isNew ? "Discard changes" : "Cancel" }}
        </button>
      </ng-template>
    </kendo-grid-command-column>

    <!-- Detail template -->
    <ng-template kendoGridDetailTemplate let-dataItem>
      <section>
        <p><span>ID:</span> <span>{{ dataItem.id }}</span></p>
        <p><span>Modified By:</span> <span>{{ dataItem.modifiedBy }}</span></p>
        <p><span>Modified Date:</span> <span>{{ dataItem.modifiedDate | date:'short' }}</span></p>
        <p><span>Created By:</span> <span>{{ dataItem.createdBy }}</span></p>
        <p><span>Create Date:</span> <span>{{ dataItem.createdDate | date:'short' }}</span></p>
      </section>
    </ng-template>

    <!-- Pager template -->
    <ng-template
      kendoPagerTemplate
      let-totalPages="totalPages"
      let-currentPage="currentPage">
      <kendo-pager-prev-buttons></kendo-pager-prev-buttons>
      <kendo-pager-numeric-buttons [buttonCount]="2"></kendo-pager-numeric-buttons>
      <kendo-pager-next-buttons></kendo-pager-next-buttons>
      <kendo-pager-input></kendo-pager-input>
      <kendo-pager-info></kendo-pager-info>
      <kendo-pager-page-sizes [pageSizes]="pageSizes"></kendo-pager-page-sizes>
    </ng-template>
  </kendo-grid>
} @loading {
  <p>Loading messages...</p>
} @error {
  <p>Failed to load messages.</p>
}
